import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import { getEarningSummary, listPayoutRequests, requestPayout } from "../lib/workflowService";
import type { PayoutMethod } from "../lib/workflowTypes";
import "../member-final.css";

export function EarningsPage() {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<PayoutMethod>("wise");
  const [amount, setAmount] = useState("50");
  const [destination, setDestination] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [notice, setNotice] = useState("");

  const summary = useQuery({ queryKey: ["earning-summary"], queryFn: getEarningSummary });
  const payouts = useQuery({ queryKey: ["payout-requests"], queryFn: listPayoutRequests });
  const minimumPayout = summary.data?.minimumPayoutUsd ?? 50;
  const availableUsd = summary.data?.availableUsd ?? 0;
  const amountNumber = Number(amount);
  const request = useMutation({
    mutationFn: () => requestPayout({
      amountUsd: Number(amount),
      method,
      destination,
      destinationConfirmation: confirmation,
      guardianName: guardianName || undefined,
      guardianEmail: guardianEmail || undefined,
    }, summary.data?.guardianRequired ?? false),
    onSuccess: async () => {
      setNotice(summary.data?.guardianRequired ? "Request saved. Your guardian must approve before payment." : "Payout request sent for manual verification.");
      setDestination("");
      setConfirmation("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["earning-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["payout-requests"] }),
      ]);
    },
  });

  const destinationLabel = method === "wise" ? "Wise account email" : method === "paypal" ? "PayPal email" : "USDT Tron wallet address";
  const canRequest = amountNumber >= minimumPayout
    && amountNumber <= availableUsd
    && destination.length >= 5
    && destination === confirmation
    && (!summary.data?.guardianRequired || (guardianName.trim().length >= 2 && guardianEmail.includes("@")));
  const amountHelp = availableUsd >= minimumPayout
    ? `You can request $${minimumPayout.toFixed(2)}–$${availableUsd.toFixed(2)} right now.`
    : `Payouts open at $${minimumPayout.toFixed(2)}. Current available balance is $${availableUsd.toFixed(2)}.`;

  return (
    <div className="member-app standalone-member-page">
      <header className="member-header">
        <Link className="member-logo" to="/app"><span>H</span><b>THE HOUSE</b></Link>
        <div className="member-header-actions"><NavLink to="/app" end>OFFERS</NavLink><NavLink to="/app/training">TRAINING</NavLink></div>
      </header>
      <main>
        <section className="standalone-member-hero">
          <span>USD EARNINGS</span>
          <h1>Your work.<br />Your balance.</h1>
          <p>Approved earnings become available after settlement clears. Payouts are manually verified before payment is marked complete.</p>
        </section>
        <section className="earnings-cards">
          <div><span>AVAILABLE</span><strong>${(summary.data?.availableUsd ?? 0).toFixed(2)}</strong><small>CAN REQUEST</small></div>
          <div><span>PENDING</span><strong>${(summary.data?.pendingUsd ?? 0).toFixed(2)}</strong><small>UNDER REVIEW / HOLD</small></div>
          <div><span>REQUESTED</span><strong>${(summary.data?.requestedUsd ?? 0).toFixed(2)}</strong><small>IN PAYOUT QUEUE</small></div>
          <div><span>PAID</span><strong>${(summary.data?.paidUsd ?? 0).toFixed(2)}</strong><small>LIFETIME</small></div>
        </section>
        {notice && <StatusPanel title="Payout requested" message={notice} kind="success" />}
        {request.isError && <StatusPanel title="Payout blocked" message={request.error.message} kind="error" />}
        <section className="payout-layout">
          <div className="payout-form">
            <div className="member-section-head"><div><span>MINIMUM ${minimumPayout.toFixed(0)}</span><h2>Request payout</h2></div></div>
            <p className="payout-helper">{amountHelp}</p>
            {availableUsd >= minimumPayout && <button className="payout-max" type="button" onClick={() => setAmount(availableUsd.toFixed(2))}>REQUEST FULL BALANCE</button>}
            <label><span>Amount in USD</span><input type="number" min={minimumPayout} max={availableUsd} value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
            <fieldset><legend>Method</legend>
              <label><input type="radio" name="method" checked={method === "wise"} onChange={() => setMethod("wise")} />Wise</label>
              <label><input type="radio" name="method" checked={method === "paypal"} onChange={() => setMethod("paypal")} />PayPal</label>
              <label><input type="radio" name="method" checked={method === "usdt_tron"} onChange={() => setMethod("usdt_tron")} />USDT on Tron</label>
            </fieldset>
            {method === "usdt_tron" && <StatusPanel title="Check the network" message="Only send a Tron (TRC20) address. A wrong network can permanently lose funds." />}
            <p className="payout-helper">Manual review normally verifies the destination, balance reservation, and provider reference before a payout is marked paid.</p>
            <label><span>{destinationLabel}</span><input value={destination} autoComplete="off" onChange={(event) => setDestination(event.target.value)} /></label>
            <label><span>Confirm {destinationLabel.toLowerCase()}</span><input value={confirmation} autoComplete="off" onChange={(event) => setConfirmation(event.target.value)} /></label>
            {summary.data?.guardianRequired && <div className="guardian-payout"><h3>Guardian payout approval</h3><p>Because you are under 18, your balance stays locked until your guardian approves this request.</p><label><span>Guardian full name</span><input value={guardianName} onChange={(event) => setGuardianName(event.target.value)} /></label><label><span>Guardian email</span><input type="email" value={guardianEmail} onChange={(event) => setGuardianEmail(event.target.value)} /></label></div>}
            <button className="payout-submit" disabled={!canRequest || request.isPending} onClick={() => request.mutate()}>{request.isPending ? "SENDING…" : "REQUEST PAYOUT"}</button>
          </div>
          <div className="payout-history">
            <div className="member-section-head"><div><span>AUDIT TRAIL</span><h2>Requests</h2></div></div>
            {(payouts.data ?? []).map((item) => <article key={item.id}><div><b>${item.amountUsd.toFixed(2)} · {item.method.replace("_", " ").toUpperCase()}</b><span>{item.destinationMasked}</span></div><div><strong>{item.status.replace("_", " ")}</strong><time>{new Date(item.requestedAt).toLocaleString()}</time></div></article>)}
            {!payouts.isLoading && !payouts.data?.length && <div className="member-empty"><span>$</span><h3>No payout requests yet.</h3><p>This audit trail starts with requests made in this app. Lifetime paid can include imported historical payouts.</p></div>}
          </div>
        </section>
      </main>
    </div>
  );
}
